/**
 * Web-shell UX kernel (D319): the framework-neutral logic behind the Vello web navigation + listing standard
 * (Claude Design "Admin Web - Passageiros Interativo", shell 1a — the standard for the Admin Web and every later
 * Vello web system). Pure and tested: column-picker state (toggle / reorder / persisted codec with a locked first
 * column), the breadcrumb derived from the navigation tree, directory status filters, CSV serialization
 * (RFC 4180), initials, and paging labels. Screens compose these; nothing here touches the DOM or the network.
 */
import { type DirectoryStatus, type DirectoryUser } from '@vello/api-client';
import { PHONE_DISPLAY_RULES } from '@vello/country-vocabularies';
import { formatPhone } from '@vello/format';

// ---- column picker ----------------------------------------------------------------------------------

export interface ColumnDef {
  readonly id: string;
  /** Message id of the column's name (the picker) and header (uppercase in the list). */
  readonly labelId: string;
  /** The CSS grid track for the column (`2fr`, `176px`, …). */
  readonly width: string;
  /** A locked column is always on and always first (the design's "Pessoa e ações são fixas"). */
  readonly locked?: boolean;
  /** Off by default (the operator turns it on from the picker). */
  readonly defaultOff?: boolean;
}

export interface ColumnState {
  readonly id: string;
  readonly on: boolean;
}

export function defaultColumnState(defs: readonly ColumnDef[]): readonly ColumnState[] {
  return defs.map((d) => ({ id: d.id, on: d.locked === true || d.defaultOff !== true }));
}

function defOf(defs: readonly ColumnDef[], id: string): ColumnDef | undefined {
  return defs.find((d) => d.id === id);
}

/** Toggle a column's visibility; a locked column never changes. */
export function toggleColumn(state: readonly ColumnState[], defs: readonly ColumnDef[], id: string): readonly ColumnState[] {
  if (defOf(defs, id)?.locked === true) return state;
  return state.map((c) => (c.id === id ? { ...c, on: !c.on } : c));
}

/** Move a column one slot up (-1) or down (+1); locked columns keep their slot and nothing moves across them. */
export function moveColumn(state: readonly ColumnState[], defs: readonly ColumnDef[], id: string, dir: -1 | 1): readonly ColumnState[] {
  const i = state.findIndex((c) => c.id === id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= state.length) return state;
  if (defOf(defs, id)?.locked === true || defOf(defs, state[j]!.id)?.locked === true) return state;
  const next = state.slice();
  [next[i], next[j]] = [next[j]!, next[i]!];
  return next;
}

/** Whether `id` may move in `dir` (drives the picker's arrow affordance). */
export function canMoveColumn(state: readonly ColumnState[], defs: readonly ColumnDef[], id: string, dir: -1 | 1): boolean {
  return moveColumn(state, defs, id, dir) !== state;
}

export function encodeColumnState(state: readonly ColumnState[]): string {
  return JSON.stringify(state.map((c) => ({ id: c.id, on: c.on })));
}

/**
 * Decode a persisted picker state; anything that does not describe exactly the known columns (same ids, one
 * entry each) falls back to the default so a stale or tampered value can never hide or duplicate a column. A
 * locked column is forced on.
 */
export function decodeColumnState(raw: string | null | undefined, defs: readonly ColumnDef[]): readonly ColumnState[] {
  const fallback = defaultColumnState(defs);
  if (raw === null || raw === undefined || raw === '') return fallback;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length !== defs.length) return fallback;
    const ids = new Set<string>();
    const out: ColumnState[] = [];
    for (const item of parsed) {
      if (typeof item !== 'object' || item === null) return fallback;
      const { id, on } = item as { id?: unknown; on?: unknown };
      if (typeof id !== 'string' || defOf(defs, id) === undefined || ids.has(id)) return fallback;
      ids.add(id);
      out.push({ id, on: defOf(defs, id)?.locked === true ? true : on === true });
    }
    // A locked column always leads.
    const lockedFirst = defs.find((d) => d.locked === true);
    if (lockedFirst !== undefined && out[0]?.id !== lockedFirst.id) return fallback;
    return out;
  } catch {
    return fallback;
  }
}

export function visibleColumns(state: readonly ColumnState[], defs: readonly ColumnDef[]): readonly ColumnDef[] {
  return state.filter((c) => c.on).map((c) => defOf(defs, c.id)).filter((d): d is ColumnDef => d !== undefined);
}

/** The list's CSS grid template — the visible column tracks plus the fixed actions track. */
export function gridTemplate(cols: readonly ColumnDef[], actionsWidth: string): string {
  return [...cols.map((c) => c.width), actionsWidth].join(' ');
}

// ---- breadcrumb from the navigation tree ------------------------------------------------------------

export interface NavTreeNode {
  readonly id: string;
  readonly labelId: string;
  readonly route?: string;
  readonly children?: readonly NavTreeNode[];
}

export interface Breadcrumb {
  /** The nearest ancestor group (or the home label when the leaf is top-level). */
  readonly parentLabelId: string;
  readonly labelId: string;
}

/** The breadcrumb for a route: `parent › page`. Unknown routes resolve to the home label alone. */
export function breadcrumbFor(tree: readonly NavTreeNode[], route: string, homeLabelId = 'nav.home'): Breadcrumb {
  const walk = (nodes: readonly NavTreeNode[], parent: NavTreeNode | undefined): Breadcrumb | undefined => {
    for (const n of nodes) {
      if (n.route === route) return { parentLabelId: parent?.labelId ?? homeLabelId, labelId: n.labelId };
      if (n.children !== undefined) {
        const found = walk(n.children, n);
        if (found !== undefined) return found;
      }
    }
    return undefined;
  };
  return walk(tree, undefined) ?? { parentLabelId: homeLabelId, labelId: homeLabelId };
}

/** Every navigable leaf with its parent label — the command palette's "Telas" section. */
export function navLeaves(tree: readonly NavTreeNode[], homeLabelId = 'nav.home'): readonly { route: string; labelId: string; parentLabelId: string }[] {
  const out: { route: string; labelId: string; parentLabelId: string }[] = [];
  const walk = (nodes: readonly NavTreeNode[], parent: NavTreeNode | undefined): void => {
    for (const n of nodes) {
      if (n.route !== undefined) out.push({ route: n.route, labelId: n.labelId, parentLabelId: parent?.labelId ?? homeLabelId });
      if (n.children !== undefined) walk(n.children, n);
    }
  };
  walk(tree, undefined);
  return out;
}

/** The ids of the groups that contain `route` (so the sidebar opens its disclosure path). */
export function ancestorsOf(tree: readonly NavTreeNode[], route: string): readonly string[] {
  const walk = (nodes: readonly NavTreeNode[], trail: readonly string[]): readonly string[] | undefined => {
    for (const n of nodes) {
      if (n.route === route) return trail;
      if (n.children !== undefined) {
        const found = walk(n.children, [...trail, n.id]);
        if (found !== undefined) return found;
      }
    }
    return undefined;
  };
  return walk(tree, []) ?? [];
}

/** Case- and accent-insensitive "contains" for palette/filter matching. */
export function textMatches(haystack: string, query: string): boolean {
  const norm = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  return norm(haystack).includes(norm(query.trim()));
}

// ---- directory helpers ------------------------------------------------------------------------------

export type DirectoryFilter = 'all' | 'active' | 'suspended' | 'deleted';
export const DIRECTORY_FILTERS: readonly DirectoryFilter[] = ['all', 'active', 'suspended', 'deleted'];

/** The server status a segmented filter maps to (`all` → no filter). */
export function statusForFilter(filter: DirectoryFilter): DirectoryStatus | undefined {
  switch (filter) {
    case 'active':
      return 'ACTIVE';
    case 'suspended':
      return 'SUSPENDED';
    case 'deleted':
      return 'DELETED';
    default:
      return undefined;
  }
}

export function statusTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 'ACTIVE') return 'success';
  if (status === 'SUSPENDED') return 'warning';
  if (status === 'DELETED') return 'danger';
  return 'neutral';
}

export function personName(u: Pick<DirectoryUser, 'firstName' | 'lastName' | 'phoneE164' | 'phoneMasked'>, homeCountry?: string): string {
  const name = [u.firstName, u.lastName].filter((p) => p !== undefined && p !== '').join(' ');
  return name !== '' ? name : shownPhone(u, homeCountry);
}

/**
 * A phone as a person reads it (D580): the E.164 the platform stores, written in the country pack's own pattern — national
 * when the phone belongs to the operator's scope country (`homeCountry`), international otherwise, `+CC rest` for a country
 * with no installed pack. A masked phone (the server withheld digits) and anything malformed stay exactly as received.
 */
export function phoneText(raw: string | undefined, homeCountry?: string): string {
  return formatPhone(raw, { rules: PHONE_DISPLAY_RULES, homeCountry });
}

/**
 * The phone a directory row shows (D384): the whole one only reaches an operator with `account:manage`; everyone else
 * receives the masked one from the server. The console never masks or unmasks by itself.
 */
export function shownPhone(u: Pick<DirectoryUser, 'phoneE164' | 'phoneMasked'>, homeCountry?: string): string {
  const raw = u.phoneE164 ?? u.phoneMasked;
  return raw === undefined ? '—' : phoneText(raw, homeCountry);
}

/** The email a directory row shows (whole or masked, as the server sent it), or undefined when none. */
export function shownEmail(u: Pick<DirectoryUser, 'email' | 'emailMasked'>): string | undefined {
  return u.email ?? u.emailMasked;
}

/**
 * Two-letter initials from a name; digits-only names (a phone) fall back to the given text. A bracketed qualifier ("Pousada Teste
 * (balcão)") is not part of the name, and a word's leading punctuation is never an initial (D845 LT-008).
 */
export function initialsOf(name: string, fallback = '·'): string {
  const wordsOf = (text: string): string[] =>
    text
      .split(/\s+/)
      .map((w) => w.replace(/^[^\p{L}\p{N}]+/u, ''))
      .filter((w) => /\p{L}/u.test(w));
  const unbracketed = wordsOf(name.replace(/\([^)]*\)?/g, ' '));
  const parts = unbracketed.length > 0 ? unbracketed : wordsOf(name);
  if (parts.length === 0) return fallback;
  const first = parts[0]!;
  const last = parts.length > 1 ? parts[parts.length - 1]! : '';
  return (first[0]! + (last[0] ?? '')).toUpperCase();
}

// ---- CSV (RFC 4180) ----------------------------------------------------------------------------------

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Serialize rows to CSV with CRLF line ends; every field is quoted only when it needs to be. */
export function csvFor(headers: readonly string[], rows: readonly (readonly string[])[]): string {
  return [headers, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

// ---- paging ------------------------------------------------------------------------------------------

export interface PageRange {
  readonly from: number;
  readonly to: number;
  readonly total: number;
  readonly hasPrev: boolean;
  readonly hasNext: boolean;
}

export function pageRange(offset: number, pageSize: number, total: number): PageRange {
  if (total <= 0) return { from: 0, to: 0, total: 0, hasPrev: false, hasNext: false };
  const from = Math.min(offset + 1, total);
  const to = Math.min(offset + pageSize, total);
  return { from, to, total, hasPrev: offset > 0, hasNext: offset + pageSize < total };
}
